/**
 * preFillForm Module
 *
 * This module is responsible for pre-filling a form with user information
 * stored in local storage and updating that information when a valid lead
 * event is triggered. It utilizes AES-GCM encryption to securely store
 * user data.
 *
 * Usage:
 *
 * 1. Data Attributes for Filling Inputs:
 *    - Use the following data attributes in your form inputs to specify
 *      which fields should be filled:
 *      - `data-form-name-input`: Input for the user's full name.
 *      - `data-form-email-input`: Input for the user's email address.
 *      - `data-form-phone-input`: Input for the user's phone number.
 *      - `data-form-zip-input`: Input for the user's postal code.
 *      - `data-form-lives-outside-input`: Checkbox indicating if the user
 *        lives outside the specified area.
 *
 * 2. Valid Lead Event for Updating Info:
 *    - Dispatch a custom event named 'valid-lead' on the document body
 *      when the form is valid and ready to update user information.
 *      The event should include the form data in the `detail` property.
 *
 * Example:
 *
 * const leadForm = document.querySelector('[data-lead-form]');
 * leadForm.dispatchEvent(new CustomEvent('valid-lead', { detail: { form: leadForm } }));
 */
(async function preFillForm() {
  'use strict';

  const cryptoKeyName = 'CryptoKey';
  const preFillFormKey = 'PrefillForm';

  /**
   * Encrypts the provided data using AES-GCM with a generated IV and a crypto key retrieved from storage.
   *
   * @async
   * @function encryptData
   * @param {Object} data - The data to be encrypted.
   * @param {any} storage - The storage mechanism used to retrieve the crypto key.
   * @returns {Promise<{cipher: string, iv: number[]}>} An object containing the base64-encoded cipher text and
   * the initialization vector (IV) as an array of numbers.
   */
  const encryptData = async (data, key) => {
    const initializationVectorLength = 12;
    const iv = crypto.getRandomValues(new Uint8Array(initializationVectorLength));
    const encoded = new TextEncoder().encode(JSON.stringify(data));

    const cipherBuffer = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      encoded
    );

    return {
      cipher: btoa(String.fromCharCode(...new Uint8Array(cipherBuffer))),
      iv: Array.from(iv),
    };
  };

  /**
   * Decrypts AES-GCM encrypted data using a provided initialization vector and storage for the key.
   *
   * @async
   * @function decryptData
   * @param {string} cipher - The base64-encoded ciphertext to decrypt.
   * @param {Uint8Array|Array|number[]} initVector - The initialization vector (IV) used for decryption.
   * @param {any} storage - The storage object or identifier used to retrieve the crypto key.
   * @returns {Promise<Object>} The decrypted data as a JavaScript object.
   */
  const decryptData = async (cipher, initVector, key) => {
    const cipherBytes = Uint8Array.from(atob(cipher), (char) => char.charCodeAt(0));

    const plainBuffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: new Uint8Array(initVector) },
      key,
      cipherBytes
    );

    return JSON.parse(new TextDecoder().decode(plainBuffer));
  };

  /**
   * Retrieves basic user information from the provided form.
   * This function is currently returning hardcoded values for testing purposes.
   * In the future, it will be updated to return real user data from the form.
   *
   * @param {HTMLFormElement} leadForm - The form element from which to extract user information.
   * @returns {Object} An object containing user information such as email, name, phone, zip code, and residency status.
   */
  const getUserBasicInformation = (leadForm) => {
    const fullNameInput = leadForm.querySelector('[data-form-name-input]');
    const emailAddressInput = leadForm.querySelector('[data-form-email-input]');
    const phoneNumberInput = leadForm.querySelector('[data-form-phone-input]');
    const postalCodeInput = leadForm.querySelector('[data-form-zip-input]');
    const livesOutsideInput = leadForm.querySelector('[data-form-lives-outside-input]');

    const userInformation = {
      email: '',
      name: '',
      phone: '',
      zip: '',
      livesOutside: false,
    };

    if (fullNameInput) {
      userInformation.name = fullNameInput.value || '';
    }

    if (emailAddressInput) {
      userInformation.email = emailAddressInput.value || '';
    }

    if (phoneNumberInput) {
      userInformation.phone = phoneNumberInput.value || '';
    }

    if (postalCodeInput) {
      userInformation.zip = postalCodeInput.value || '';
    }

    if (livesOutsideInput) {
      userInformation.livesOutside = livesOutsideInput.checked;
    }

    return userInformation;
  };

  /**
   * Updates user information by retrieving data from the provided form,
   * encrypting it, and storing it in local storage. This function is triggered
   * by a custom event 'valid-lead' and handles the encryption of user data
   * using AES-GCM.
   *
   * @async
   * @function updateUserInformation
   * @param {CustomEvent} event - The event object containing the form data
   *                              in the detail property.
   * @returns {Promise<void>} A promise that resolves when the user information
   *                          has been successfully encrypted and stored.
   */
  const updateUserInformation = async (userInformation) => {
    try {
      const isKeyStored = localStorage.getItem(cryptoKeyName) !== null;

      const key = isKeyStored
        ? await crypto.subtle.importKey(
          'jwk',
          JSON.parse(localStorage.getItem(cryptoKeyName)),
          { name: 'AES-GCM' },
          true,
          ['encrypt', 'decrypt']
        )
        : await crypto.subtle.generateKey(
          { name: 'AES-GCM', length: 256 },
          true,
          ['encrypt', 'decrypt']
        );

      if (!isKeyStored) {
        const exported = await crypto.subtle.exportKey('jwk', key);
        localStorage.setItem(cryptoKeyName, JSON.stringify(exported));
      }

      const encrypted = await encryptData(userInformation, key);
      localStorage.setItem(preFillFormKey, JSON.stringify(encrypted));
    } catch (e) {
      // intentionally ignored
    }
  };

  /**
   * Pre-fills the form with decrypted user information stored in local storage.
   *
   * @async
   * @function preFill
   * @param {HTMLFormElement} leadForm - The form element to be pre-filled with user information.
   * @returns {Promise<void>} A promise that resolves when the form has been pre-filled.
   */
  const preFill = async (leadForm) => {
    if (!localStorage.getItem(cryptoKeyName)) {
      return;
    }

    try {
      const importedKey = await crypto.subtle.importKey(
        'jwk',
        JSON.parse(localStorage.getItem(cryptoKeyName)),
        { name: 'AES-GCM' },
        true,
        ['encrypt', 'decrypt']
      );

      const prefillInformation = JSON.parse(localStorage.getItem(preFillFormKey));

      if (!prefillInformation) {
        return;
      }

      const decrypted = await decryptData(prefillInformation.cipher, prefillInformation.iv, importedKey);

      if (!decrypted) {
        return;
      }

      const fullNameInput = leadForm.querySelector('[data-form-name-input]');
      const emailAddressInput = leadForm.querySelector('[data-form-email-input]');
      const phoneNumberInput = leadForm.querySelector('[data-form-phone-input]');
      const postalCodeInput = leadForm.querySelector('[data-form-zip-input]');
      const livesOutsideInput = leadForm.querySelector('[data-form-lives-outside-input]');

      if (fullNameInput) {
        fullNameInput.value = decrypted.name || '';
      }

      if (emailAddressInput) {
        emailAddressInput.value = decrypted.email || '';
      }

      if (phoneNumberInput) {
        phoneNumberInput.value = decrypted.phone || '';
      }

      if (postalCodeInput) {
        postalCodeInput.value = decrypted.zip || '';
      }

      if (livesOutsideInput) {
        livesOutsideInput.checked = decrypted.livesOutside;
        postalCodeInput.disabled = decrypted.livesOutside;
      }
    } catch (e) {
      // intentionally ignored
    }
  };

  /**
   * Updates all forms on the page by pre-filling them with user information
   * stored in local storage. This function retrieves all forms with the
   * 'data-lead-form' attribute and applies the pre-fill logic to each.
   *
   * @async
   * @function updateOnPageForms
   * @returns {Promise<void>} A promise that resolves when all forms have been updated.
   */
  const updateOnPageForms = async () => {
    const formsOnPage = document.querySelectorAll('[data-lead-form]');
    await Promise.all(
      Array.from(formsOnPage).map((form) => preFill(form))
    );
  };

  /**
   * Handles the 'valid-lead' event to update user information and pre-fill forms.
   *
   * @async
   * @function onValidLead
   * @param {CustomEvent} event - The event object containing the form data
   *                              in the detail property.
   * @returns {Promise<void>} A promise that resolves when user information
   *                          has been updated and forms have been pre-filled.
   */
  const onValidLead = async (event) => {
    if (!event.detail || !event.detail.form) {
      return;
    }

    const userInformation = getUserBasicInformation(event.detail.form);
    await updateUserInformation(userInformation);
    await updateOnPageForms();
  };

  /**
   * Handles the event when a modal window is opened.
   * This function checks if the modal contains a form with the 'data-lead-form' attribute
   * and pre-fills it with user information if applicable.
   *
   * @async
   * @function onWindowModalOpened
   * @param {CustomEvent} event - The event object containing details about the modal.
   * @returns {Promise<void>} A promise that resolves when the form has been pre-filled.
   */
  const onWindowModalOpened = async (event) => {
    const modal = event.detail.modal;

    if (!modal || !modal.matches('[data-lead-form]')) {
      return;
    }

    await preFill(modal);
  };

  /**
   * Handles user authentication event to update user information.
   *
   * @async
   * @function onAuthenticatedUser
   * @param {CustomEvent} event - The event object containing user data.
   * @returns {Promise<void>} A promise that resolves when user information is updated.
   */
  const onAuthenticatedUser = async (event) => {
    const userData = event.detail.userData;
    if (!userData) {
      return;
    }

    const userInformation = {
      email: userData.Email || '',
      name: userData.FirstName + ' ' + userData.LastName,
      zip: userData.PostalCode || '',
      phone: '',
      livesOutside: false,
    };

    await updateUserInformation(userInformation);
  };

  /**
   * Event listener for the 'valid-lead' custom event.
   *
   * This listener is responsible for updating user information in local storage
   * when a valid lead event is triggered. It listens for the 'valid-lead' event
   * dispatched on the document body, which contains the form data in its detail
   * property. When the event is detected, the `updateUserInformation` function
   * is called to handle the encryption and storage of the user data.
   */
  document.body.addEventListener('valid-lead', onValidLead);
  document.addEventListener('modalOpened', onWindowModalOpened);
  document.body.addEventListener('loginSuccess', onAuthenticatedUser);

  await updateOnPageForms();

  /**
   * Handles a DOM node to check if it is a form with the 'data-lead-form' attribute
   * and pre-fills it with user information if applicable.
   *
   * @param {Node} node - The DOM node to handle.
   * @returns {void}
   */
  const handleNode = (node) => {
    if (
      node.nodeType === 1 &&
      (node.hasAttribute('data-lead-form') || node.querySelector('[data-lead-form]'))
    ) {
      preFill(node);
    }
  };

  const observer = new MutationObserver((mutations) => {
    for (let i = 0; i < mutations.length; i += 1) {
      const mutation = mutations[i];

      if (mutation.addedNodes && mutation.addedNodes.length > 0) {
        for (let j = 0; j < mutation.addedNodes.length; j += 1) {
          const added = mutation.addedNodes[j];
          handleNode(added);
        }
      }
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
}());

define("prefillForm", function(){});

